import type { KanbanBoardItem, KanbanBoardDetail, KanbanMember, KanbanUser, KanbanCard, KanbanColumnKey, EcheancesResponse, MyEcheancesResponse, GeranceHistory, InvolvementItem, HomeData, HomeProjectDetail } from '../types';

import { getLiveConn } from '../live/live';

const API_BASE = '/api';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      // This tab's live stream: its own saves aren't sent back to it as changes.
      ...(getLiveConn() ? { 'X-Live-Conn': getLiveConn()! } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    // Keep the server's machine-readable code (e.g. TASK_PRIVATE) and the rest of its body for callers.
    throw Object.assign(new Error(error.error || 'Request failed'), { code: error.code as string | undefined, details: error });
  }

  // A save may have queued e-mails: let the topbar's pending-e-mails button refresh now.
  if (options.method && options.method !== 'GET') {
    setTimeout(() => window.dispatchEvent(new Event('pmo:data-changed')), 300);
  }

  return response.json();
}

export const api = {
  // Projects
  getProjects: () => request('/projects'),
  getProject: (id: number) => request(`/projects/${id}`),
  createProject: (data: any) => request('/projects', { method: 'POST', body: JSON.stringify(data) }),
  updateProject: (id: number, data: any) => request(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateProjectStatus: (id: number, statusId: number) =>
    request(`/projects/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status_id: statusId }) }),
  setDeferredReview: (id: number, state: string) => request(`/projects/${id}/deferred-review`, { method: 'PATCH', body: JSON.stringify({ deferred_review: state }) }),
  updateProjectDomain: (id: number, domainId: number | null) =>
    request(`/projects/${id}/domain`, { method: 'PATCH', body: JSON.stringify({ domain_id: domainId }) }),
  updateProjectPlacement: (id: number, domainId: number | null, levelId: number | null) =>
    request(`/projects/${id}/placement`, { method: 'PATCH', body: JSON.stringify({ domain_id: domainId, level_id: levelId }) }),
  deleteProject: (id: number) => request(`/projects/${id}`, { method: 'DELETE' }),

  // Pre-qualification items
  getProjectItems: (projectId: number) => request(`/projects/${projectId}/items`),
  addProjectItem: (projectId: number, content: string) =>
    request(`/projects/${projectId}/items`, { method: 'POST', body: JSON.stringify({ content }) }),
  updateProjectItem: (projectId: number, itemId: number, content: string) =>
    request(`/projects/${projectId}/items/${itemId}`, { method: 'PUT', body: JSON.stringify({ content }) }),
  deleteProjectItem: (projectId: number, itemId: number) =>
    request(`/projects/${projectId}/items/${itemId}`, { method: 'DELETE' }),

  // Project Privacy
  setProjectPrivacy: (projectId: number, isPrivate: boolean) =>
    request(`/projects/${projectId}/privacy`, { method: 'PATCH', body: JSON.stringify({ is_private: isPrivate }) }),

  // Project Blocked Mode
  setProjectBlocked: (projectId: number, isBlocked: boolean, reason: string | null) =>
    request(`/projects/${projectId}/blocked`, { method: 'PATCH', body: JSON.stringify({ is_blocked: isBlocked, blocked_reason: reason }) }),

  // Grid config
  getGridColumns: (gridType: 'portfolio' | 'pipeline' | 'kanban') => request(`/grid/columns/${gridType}`),
  createGridColumn: (gridType: string, label: string, color?: string) =>
    request('/grid/columns', { method: 'POST', body: JSON.stringify({ gridType, label, color }) }),
  updateGridColumn: (id: number, label: string, color?: string) =>
    request(`/grid/columns/${id}`, { method: 'PUT', body: JSON.stringify({ label, color }) }),
  deleteGridColumn: (id: number) => request(`/grid/columns/${id}`, { method: 'DELETE' }),
  reorderGridColumns: (gridType: string, order: number[]) =>
    request(`/grid/columns-order/${gridType}`, { method: 'PUT', body: JSON.stringify({ order }) }),

  getGridRows: () => request('/grid/rows'),
  createGridRow: (label: string) =>
    request('/grid/rows', { method: 'POST', body: JSON.stringify({ label }) }),
  updateGridRow: (id: number, label: string) =>
    request(`/grid/rows/${id}`, { method: 'PUT', body: JSON.stringify({ label }) }),
  deleteGridRow: (id: number) => request(`/grid/rows/${id}`, { method: 'DELETE' }),
  reorderGridRows: (order: number[]) =>
    request('/grid/rows-order', { method: 'PUT', body: JSON.stringify({ order }) }),

  // Admin
  getSettings: () => request('/admin/settings'),
  refreshDevFromProd: () => request('/admin/db-refresh', { method: 'POST' }),
  migrateStages: () => request('/admin/migrate-stages', { method: 'POST' }),
  updateSetting: (key: string, value: string) =>
    request(`/admin/settings/${key}`, { method: 'PUT', body: JSON.stringify({ value }) }),
  getStats: () => request('/admin/stats'),

  // Lookups (autosuggest)
  getPeople: (q?: string) => request(`/lookups/people${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  createPerson: (name: string) => request('/lookups/people', { method: 'POST', body: JSON.stringify({ name }) }),

  getUserGroups: (q?: string) => request(`/lookups/user-groups${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  createUserGroup: (name: string) => request('/lookups/user-groups', { method: 'POST', body: JSON.stringify({ name }) }),

  getInfrastructure: (q?: string) => request(`/lookups/infrastructure${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  createInfrastructure: (name: string) => request('/lookups/infrastructure', { method: 'POST', body: JSON.stringify({ name }) }),
  updateInfrastructure: (id: number, name: string) =>
    request(`/lookups/infrastructure/${id}`, { method: 'PUT', body: JSON.stringify({ name }) }),
  deleteInfrastructure: (id: number) => request(`/lookups/infrastructure/${id}`, { method: 'DELETE' }),

  // People CRUD (extended)
  getPersonDetail: (id: number) => request(`/lookups/people/${id}`),
  createFullPerson: (data: { first_name: string; last_name: string; email?: string }) =>
    request('/lookups/people', { method: 'POST', body: JSON.stringify(data) }),
  updatePerson: (id: number, data: any) =>
    request(`/lookups/people/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deletePerson: (id: number) =>
    request(`/lookups/people/${id}`, { method: 'DELETE' }),
  createUserFromPerson: (personId: number) =>
    request(`/lookups/people/${personId}/create-user`, { method: 'POST' }),

  // Inventories (aggregated views)
  getInventoryPeople: () => request('/inventories/people'),
  getInventoryInfrastructure: () => request('/inventories/infrastructure'),
  getInventoryDeliverables: (status?: string) =>
    request(`/inventories/deliverables${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  updateDeliverable: (projectId: number, index: number, title: string, body: string) =>
    request(`/projects/${projectId}/deliverable`, { method: 'PATCH', body: JSON.stringify({ index, title, body }) }),

  // Calendar sync
  getCalendarStatus: () => request('/calendar/status'),
  getProjectEvents: (projectId: number) => request(`/calendar/events/${projectId}`),
  createCalendarEvent: (data: any) => request('/calendar/events', { method: 'POST', body: JSON.stringify(data) }),
  updateCalendarEvent: (id: number, data: any) => request(`/calendar/events/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCalendarEvent: (id: number) => request(`/calendar/events/${id}`, { method: 'DELETE' }),
  triggerCalendarSync: () => request('/calendar/sync', { method: 'POST' }),

  // Calendar admin
  getCalendarAdminConfig: () => request('/calendar/admin/config'),
  saveCalendarConfig: (config: { tenant_id: string; client_id: string; client_secret: string; webhook_url: string }) =>
    request('/calendar/admin/config', { method: 'PUT', body: JSON.stringify(config) }),
  forceFullSync: () => request('/calendar/admin/sync', { method: 'POST' }),
  forceRematch: () => request('/calendar/admin/rematch', { method: 'POST' }),

  // Dummy calendar events (testing)
  seedDummyEvents: () => request('/calendar/admin/seed-dummy', { method: 'POST' }),
  deleteDummyEvents: () => request('/calendar/admin/seed-dummy', { method: 'DELETE' }),

  // Transcriptions / Speakr
  getProjectTimeline: (projectId: number) => request(`/transcriptions/timeline/${projectId}`),
  getTranscriptContent: (id: number) => request(`/transcriptions/${id}/transcript`),
  getTranscriptSpeakers: (id: number) => request(`/transcriptions/${id}/speakers`),
  renameTranscriptSpeaker: (id: number, label: string, name: string) =>
    request(`/transcriptions/${id}/speakers`, { method: 'PUT', body: JSON.stringify({ label, name }) }),
  redoTranscription: (id: number, speakers: number | null) =>
    request(`/transcriptions/${id}/redo`, { method: 'POST', body: JSON.stringify({ speakers }) }),
  triggerSpeakrSync: () => request('/transcriptions/sync', { method: 'POST' }),
  triggerSpeakrSyncAll: () => request('/transcriptions/sync-all', { method: 'POST' }),

  // Transcription link/unlink
  linkTranscription: (id: number, eventId: number) =>
    request(`/transcriptions/${id}/link`, { method: 'POST', body: JSON.stringify({ calendar_event_id: eventId }) }),
  unlinkTranscription: (id: number) =>
    request(`/transcriptions/${id}/unlink`, { method: 'POST' }),
  deleteTranscription: (id: number) =>
    request(`/transcriptions/${id}`, { method: 'DELETE' }),
  getLinkableEvents: (id: number) =>
    request<{ events: { id: number; subject: string; start_time: string; end_time: string }[] }>(`/transcriptions/${id}/linkable-events`),

  // Speakr user config
  getSpeakrUserConfig: () => request('/transcriptions/user-config'),
  saveSpeakrUserConfig: (data: { speakr_api_token?: string; speakr_base_url?: string }) =>
    request('/transcriptions/user-config', { method: 'PUT', body: JSON.stringify(data) }),

  // Password
  changePassword: (currentPassword: string, newPassword: string) =>
    request('/admin/users/me/password', { method: 'PUT', body: JSON.stringify({ currentPassword, newPassword }) }),

  // Speakr admin
  getSpeakrAdminConfig: () => request('/transcriptions/admin/config'),
  saveSpeakrAdminConfig: (baseUrl: string) =>
    request('/transcriptions/admin/config', { method: 'PUT', body: JSON.stringify({ speakr_base_url: baseUrl }) }),
  forceSpeakrRematch: () => request('/transcriptions/admin/rematch', { method: 'POST' }),
  backfillTranscripts: () => request('/transcriptions/admin/backfill-transcripts', { method: 'POST' }),

  // Tasks (Kanban)
  getKanbanColumns: () => request('/tasks/columns'),
  getKanbanBoard: (filters?: Record<string, string>) => {
    const params = filters ? new URLSearchParams(filters).toString() : '';
    return request(`/tasks/board${params ? `?${params}` : ''}`);
  },
  createTask: (data: { project_id: number; column_id: number; title: string; description?: string; due_date?: string; assignee_name?: string; collaborator_names?: string[]; planned_at?: string; milestone_id?: string; linked_project_ids?: number[] }) =>
    request('/tasks', { method: 'POST', body: JSON.stringify(data) }),
  getTaskDetail: (id: number) => request(`/tasks/${id}`),
  updateTask: (id: number, data: any) =>
    request(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  contactTaskCreator: (id: number, reason: string, message: string) =>
    request(`/tasks/${id}/contact-creator`, { method: 'POST', body: JSON.stringify({ reason, message }) }),
  shareProject: (id: number, userIds: number[], note: string, channels: { slack: boolean; email: boolean }, tab?: string) =>
    request<{ sent: { name: string; channels: string[] }[]; failed: { name: string; channel?: string; code: string }[] }>(
      `/projects/${id}/share`, { method: 'POST', body: JSON.stringify({ user_ids: userIds, note, tab, ...channels }) }),
  remindTaskAssignee: (id: number, note: string, channels: { slack: boolean; email: boolean }) =>
    request(`/tasks/${id}/remind`, { method: 'POST', body: JSON.stringify({ note, ...channels }) }),
  setTaskPrivacy: (id: number, isPrivate: boolean) =>
    request(`/tasks/${id}/privacy`, { method: 'PATCH', body: JSON.stringify({ is_private: isPrivate }) }),
  moveTask: (id: number, columnId: number, sortOrder?: number) =>
    request(`/tasks/${id}/move`, { method: 'PATCH', body: JSON.stringify({ column_id: columnId, sort_order: sortOrder }) }),
  setTaskBlockedReason: (id: number, reason: string | null) =>
    request(`/tasks/${id}/blocked-reason`, { method: 'PATCH', body: JSON.stringify({ blocked_reason: reason }) }),
  deleteTask: (id: number) => request(`/tasks/${id}`, { method: 'DELETE' }),
  migrateTask: (id: number, projectId: number, milestoneId: string | null) =>
    request(`/tasks/${id}/migrate`, { method: 'PATCH', body: JSON.stringify({ project_id: projectId, milestone_id: milestoneId }) }),
  addTaskDependency: (taskId: number, dependsOnTaskId: number) =>
    request(`/tasks/${taskId}/dependencies`, { method: 'POST', body: JSON.stringify({ depends_on_task_id: dependsOnTaskId }) }),
  removeTaskDependency: (taskId: number, depId: number) =>
    request(`/tasks/${taskId}/dependencies/${depId}`, { method: 'DELETE' }),
  postTaskUpdate: (taskId: number, content: string, updateType?: string, replyToId?: number, taggedUserIds?: number[]) =>
    request(`/tasks/${taskId}/updates`, { method: 'POST', body: JSON.stringify({ content, update_type: updateType, reply_to_id: replyToId, tagged_user_ids: taggedUserIds }) }),
  editTaskUpdate: (taskId: number, updateId: number, content: string) =>
    request(`/tasks/${taskId}/updates/${updateId}`, { method: 'PUT', body: JSON.stringify({ content }) }),
  deleteTaskUpdate: (taskId: number, updateId: number) =>
    request(`/tasks/${taskId}/updates/${updateId}`, { method: 'DELETE' }),
  getProjectTasks: (projectId: number, includeLinked?: boolean) =>
    request(`/tasks/project/${projectId}${includeLinked ? '?include_linked=true' : ''}`),

  // Roles & Permissions
  getRoleInfo: () => request('/roles/me'),
  // Who can be a project's PMO accompanist: the admins.
  getPmoAccompanists: () => request('/roles/pmo-accompanists') as Promise<string[]>,
  getRoleUsers: () => request('/roles/users'),
  updateUserRole: (userId: number, role: string) =>
    request(`/roles/users/${userId}`, { method: 'PUT', body: JSON.stringify({ role }) }),
  getPermissionsOverview: () => request('/roles/permissions'),

  // Taggable users for questions
  getTaggableUsers: (taskId: number) => request(`/tasks/${taskId}/taggable-users`),

  // Project-level comments
  getProjectQuestions: (projectId: number) => request(`/projects/${projectId}/questions`),
  postProjectComment: (projectId: number, content: string, opts?: { tagged_user_ids?: number[]; reply_to_id?: number; context_type?: string; context_id?: number }) =>
    request(`/projects/${projectId}/questions`, {
      method: 'POST',
      body: JSON.stringify({ content, ...opts }),
    }),
  editProjectComment: (projectId: number, commentId: number, content: string) =>
    request(`/projects/${projectId}/comments/${commentId}`, { method: 'PUT', body: JSON.stringify({ content }) }),
  deleteProjectComment: (projectId: number, commentId: number) =>
    request(`/projects/${projectId}/comments/${commentId}`, { method: 'DELETE' }),
  getProjectTaggableUsers: (projectId: number) => request(`/projects/${projectId}/taggable-users`),

  // Project events (setup changes)
  deleteProjectEvent: (projectId: number, eventId: number) =>
    request(`/projects/${projectId}/events/${eventId}`, { method: 'DELETE' }),

  // Notifications
  getNotifications: () => request('/notifications'),
  getUnreadCount: () => request<{ count: number }>('/notifications/unread-count'),
  markNotificationRead: (id: number) => request(`/notifications/${id}/read`, { method: 'PATCH' }),
  markAllNotificationsRead: () => request('/notifications/mark-all-read', { method: 'POST' }),
  getNotificationPreferences: () => request('/notifications/preferences'),
  updateNotificationPreference: (notification_type: string, enabled: boolean, sub_option?: string) =>
    request('/notifications/preferences', { method: 'PUT', body: JSON.stringify({ notification_type, enabled, sub_option }) }),

  // Change Requests
  createChangeRequest: (projectId: number, proposedChanges: Record<string, any>) =>
    request('/change-requests', { method: 'POST', body: JSON.stringify({ project_id: projectId, proposed_changes: proposedChanges }) }),
  getChangeRequests: (status?: string) =>
    request(`/change-requests${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  getChangeRequest: (id: number) => request(`/change-requests/${id}`),
  approveChangeRequest: (id: number, comment?: string, forceApply?: boolean) =>
    request(`/change-requests/${id}/approve`, { method: 'POST', body: JSON.stringify({ comment, force_apply: forceApply }) }),
  rejectChangeRequest: (id: number, comment?: string) =>
    request(`/change-requests/${id}/reject`, { method: 'POST', body: JSON.stringify({ comment }) }),

  // Attachments
  uploadAttachments: async (projectId: number, files: File[], opts?: { task_update_id?: number; context_type?: string; context_id?: number }) => {
    const formData = new FormData();
    formData.append('project_id', String(projectId));
    if (opts?.task_update_id) formData.append('task_update_id', String(opts.task_update_id));
    if (opts?.context_type) formData.append('context_type', opts.context_type);
    if (opts?.context_id) formData.append('context_id', String(opts.context_id));
    for (const file of files) formData.append('files', file);

    const response = await fetch(`${API_BASE}/attachments/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }
    return response.json();
  },

  getProjectAttachments: (projectId: number, opts?: { task_update_id?: number; context_type?: string; context_id?: number }) => {
    const params = new URLSearchParams({ project_id: String(projectId) });
    if (opts?.task_update_id) params.set('task_update_id', String(opts.task_update_id));
    if (opts?.context_type) params.set('context_type', opts.context_type);
    if (opts?.context_id) params.set('context_id', String(opts.context_id));
    return request(`/attachments?${params}`);
  },

  deleteAttachment: (id: number) => request(`/attachments/${id}`, { method: 'DELETE' }),

  // Slack admin
  getSlackConfig: () => request('/slack/config'),
  saveSlackConfig: (config: { bot_token?: string; signing_secret?: string; enabled?: boolean }) =>
    request('/slack/config', { method: 'PUT', body: JSON.stringify(config) }),
  slackCreateAllChannels: () => request('/slack/create-all-channels', { method: 'POST' }),

  // Préqualification — RCAJO inventory + AI (pistache)
  getRcajoInventory: () => request('/rcajo/inventory'),
  createRcajoResource: (p: any) => request('/rcajo/resources', { method: 'POST', body: JSON.stringify(p) }),
  createRcajoCapability: (p: any) => request('/rcajo/capabilities', { method: 'POST', body: JSON.stringify(p) }),
  recordRcajoPmoScores: (p: { project_id: number; scores: { resource_id: number; capability_id?: number | null; score: number }[] }) =>
    request('/rcajo/pmo-scores', { method: 'POST', body: JSON.stringify(p) }),
  aiPrequalStrategic: (projectId: number) => request('/ai/prequalification-strategic', { method: 'POST', body: JSON.stringify({ project_id: projectId }) }),
  aiPrequalSequencing: (projectId: number) => request('/ai/prequalification-sequencing', { method: 'POST', body: JSON.stringify({ project_id: projectId }) }),
  aiDependencyReason: (p: { project_id: number; relation: string; target_name: string; target_id?: number | null }) =>
    request('/ai/dependency-reason', { method: 'POST', body: JSON.stringify(p) }),
  getAiModels: () => request('/admin/ai-models'),
  pingRcajo: () => request('/admin/rcajo-ping'),

  // Profile (app-level preferences: language)
  getProfile: () => request<{ id: number; name: string; email: string | null; lang: 'fr' | 'en' | null; outlook_sessions: boolean }>('/profile'),
  updateProfileLang: (lang: 'fr' | 'en') => request('/profile', { method: 'PATCH', body: JSON.stringify({ lang }) }),
  // My calendar sessions copied into the person's Outlook calendar (on by default).
  updateProfileOutlookSessions: (enabled: boolean) => request('/profile', { method: 'PATCH', body: JSON.stringify({ outlook_sessions: enabled }) }),

  // Sidebar: new initiatives in the Boîte de réception since I last opened it
  getInboxNewCount: () => request<{ count: number }>('/projects/inbox/new-count'),
  markInboxSeen: () => request('/projects/inbox/seen', { method: 'POST' }),

  // Sidebar "Projets récents"
  getRecentProjects: () => request('/profile/recent-projects'),
  recordRecentProject: (projectId: number) => request('/profile/recent-projects', { method: 'POST', body: JSON.stringify({ project_id: projectId }) }),

  // E-mails waiting to go out because of my changes (topbar)
  getPendingEmails: () => request('/emails/pending'),
  sendPendingEmails: () => request('/emails/send-pending', { method: 'POST' }),
  // Editing a waiting e-mail; each returns the updated pending list.
  dismissPendingEmailItem: (email: string, projectId: number | null, key: string) =>
    request('/emails/pending/dismiss', { method: 'POST', body: JSON.stringify({ email, project_id: projectId, key }) }),
  setPendingEmailComment: (email: string, projectId: number | null, key: string, text: string) =>
    request('/emails/pending/comment', { method: 'PUT', body: JSON.stringify({ email, project_id: projectId, key, text }) }),
  addPendingEmailNote: (email: string, note: { title: string; text: string; projectId: number | null }) =>
    request('/emails/pending/notes', { method: 'POST', body: JSON.stringify({ email, title: note.title, text: note.text, project_id: note.projectId }) }),
  updatePendingEmailNote: (email: string, noteId: number, note: { title: string; text: string }) =>
    request(`/emails/pending/notes/${noteId}`, { method: 'PUT', body: JSON.stringify({ email, ...note }) }),

  // Generic helpers
  get: <T = any>(path: string) => request<T>(path),
  put: (path: string, data: any) => request(path, { method: 'PUT', body: JSON.stringify(data) }),
  patch: (path: string, data: any) => request(path, { method: 'PATCH', body: JSON.stringify(data) }),
  post: (path: string, data: any) => request(path, { method: 'POST', body: JSON.stringify(data) }),
  delete: (path: string) => request(path, { method: 'DELETE' }),

  // Planner (work-week calendar)
  /** `userId`: another person's list (PMO and PMO accompanists only). */
  plannerMyTasks: (userId?: number) => request(`/planner/my-tasks${userId ? `?user_id=${userId}` : ''}`),
  plannerCalendarUsers: () => request('/planner/calendar-users'),
  plannerOutlook: (startISO: string, endISO: string) =>
    request(`/planner/outlook?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}`),
  plannerSetPlan: (source: 'project' | 'kanban', id: number, plannedAtISO: string | null, minutes?: number) =>
    request(`/planner/${source === 'kanban' ? 'kanban-cards' : 'tasks'}/${id}/plan`, { method: 'PATCH', body: JSON.stringify({ planned_at: plannedAtISO, planned_minutes: minutes }) }),
  // My calendar: the person's own work sessions — they never change the task.
  plannerSlots: (startISO: string, endISO: string, userId?: number) =>
    request(`/planner/slots?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}${userId ? `&user_id=${userId}` : ''}`),
  plannerAddSlot: (source: 'project' | 'kanban', itemId: number, startISO: string, minutes: number) =>
    request('/planner/slots', { method: 'POST', body: JSON.stringify({ source, item_id: itemId, start_at: startISO, minutes }) }),
  plannerUpdateSlot: (id: number, changes: { start_at?: string; minutes?: number }) =>
    request(`/planner/slots/${id}`, { method: 'PATCH', body: JSON.stringify(changes) }),
  plannerDeleteSlot: (id: number) =>
    request(`/planner/slots/${id}`, { method: 'DELETE' }),
  // Standalone Kanban Boards
  kanbanBoards: (scope: 'mine' | 'all' = 'mine') =>
    request<KanbanBoardItem[]>(`/kanban-boards?scope=${scope}`),
  kanbanCreateBoard: (name: string) =>
    request<KanbanBoardItem>('/kanban-boards', { method: 'POST', body: JSON.stringify({ name }) }),
  kanbanBoard: (id: number) =>
    request<KanbanBoardDetail>(`/kanban-boards/${id}`),
  kanbanUsers: () =>
    request<KanbanUser[]>('/kanban-boards/users'),
  kanbanAddMember: (id: number, userId: number) =>
    request<KanbanMember>(`/kanban-boards/${id}/members`, { method: 'POST', body: JSON.stringify({ user_id: userId }) }),
  kanbanRemoveMember: (id: number, userId: number) =>
    request(`/kanban-boards/${id}/members/${userId}`, { method: 'DELETE' }),
  kanbanAddCard: (id: number, payload: { title: string; description?: string; column_key?: KanbanColumnKey; due_date?: string | null }) =>
    request<KanbanCard>(`/kanban-boards/${id}/cards`, { method: 'POST', body: JSON.stringify(payload) }),
  kanbanUpdateCard: (cardId: number, payload: { title?: string; description?: string | null; assignee_name?: string | null; column_key?: KanbanColumnKey; sort_order?: number; due_date?: string | null }) =>
    request<KanbanCard>(`/kanban-boards/cards/${cardId}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  kanbanDeleteCard: (cardId: number) =>
    request(`/kanban-boards/cards/${cardId}`, { method: 'DELETE' }),

  // Échéances (deadlines) — Gérance (PMO-wide, admin) and the user's own timeline
  getEcheances: () => request<EcheancesResponse>('/echeances'),
  getGeranceHistory: () => request<GeranceHistory>('/gerance-history'),
  getMyEcheances: () => request<MyEcheancesResponse>('/echeances/mine'),
  getInvolvements: () => request<{ items: InvolvementItem[] }>('/echeances/involvements'),

  // Home page (Accueil): my tasks by project, the projects I sponsor
  getHome: () => request<HomeData>('/home'),
  getHomeProject: (projectId: number) => request<HomeProjectDetail>(`/home/projects/${projectId}`),
  setDeliverableValidated: (projectId: number, deliverableId: string, validated: boolean) =>
    request(`/home/projects/${projectId}/deliverables/${encodeURIComponent(deliverableId)}/validation`, { method: validated ? 'POST' : 'DELETE' }),
};
